import { useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getAICheckerQuantityMismatches,
  sendIssueToCodeMonkey,
  type MismatchGroup,
  type QuantityMismatchesResult,
} from '../../../../../../services/admin/adminService';
import { Terminal } from 'lucide-react';
import { formatQuantityInfo } from '../../shared/quantityPrompt';
import type { TFn } from '../../types';

// "Send to code monkey" for one pakbon/factuur mismatch group. Seeds a Claude session with the
// mismatch context (both quantityInfos) under the 'quantity-mismatch' flow.
function MismatchMonkeyButton({ group, t }: { group: MismatchGroup; t: TFn }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<'idle' | 'sent' | 'error'>('idle');
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const caseInfo = [
        `@README.md @z_playbooks/roles/fixQuantities.md pakbon/factuur quantity mismatch:`,
        `Platform: ${group.platform}`,
        `Match key: ${group.matchKey}`,
        `Article ID: ${group.articleId || '-'}`,
        `Product: ${group.productName}`,
        `Pakbon quantityInfo: ${formatQuantityInfo(group.pakbonQuantityInfo)}`,
        `Factuur quantityInfo: ${formatQuantityInfo(group.factuurQuantityInfo)}`,
        `Occurrences: ${group.occurrences}`,
        `Restaurants: ${group.restaurantNames.join(', ')}`,
        `Example order: ${group.exampleOrderNumber} (${group.exampleOrderDate})`,
      ].join('\n');
      await sendIssueToCodeMonkey({
        flow: 'quantity-mismatch',
        caseInfo,
        context: {
          platform: group.platform,
          matchKey: group.matchKey,
          articleId: group.articleId,
          productName: group.productName,
          pakbonQuantityInfo: group.pakbonQuantityInfo,
          factuurQuantityInfo: group.factuurQuantityInfo,
        },
      });
      setState('sent');
      setTimeout(() => setState('idle'), 1500);
    } catch (err) {
      console.error('Failed to send mismatch to code-monkey', err);
      setState('error');
      setTimeout(() => setState('idle'), 2000);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      title={t('adminTesting.review.mismatchSendMonkey')}
      className={cn('inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs whitespace-nowrap transition-colors disabled:opacity-50',
        'bg-gray-100 dark:bg-gray-700 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/30')}
    >
      <Terminal className="w-3.5 h-3.5" />
      {busy ? '…' : state === 'sent' ? t('adminTesting.review.menuCodeMonkeySent') : state === 'error' ? '✕' : t('adminTesting.review.mismatchSendMonkey')}
    </button>
  );
}

// Tab 3: pakbon/factuur quantity mismatches, grouped by platform so "which platforms need the
// enrichment fix" reads off the page. Not per-mailbox — fetches by date range only.
// Stays lazily mounted (the query scans every linked pakbon/factuur pair in the range), but reports its
// group count up via `onCount` so the tab strip can show a badge once the tab has been opened.
export function AICheckerMismatches({ startDate, endDate, onCount, t }: { startDate: string; endDate: string; onCount: (n: number) => void; t: TFn }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QuantityMismatchesResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await getAICheckerQuantityMismatches(startDate, endDate);
        if (!cancelled) { setData(r); onCount(r.groups.length); }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load mismatches');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate, onCount]);

  if (loading) return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.mismatchLoading')}</div>;
  if (error) return <div className={cn('text-center py-8 text-red-500 dark:text-textRed-dark', smallTextSize)}>{error}</div>;
  if (!data || data.groups.length === 0) {
    return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>🎉 {t('adminTesting.review.mismatchEmpty')}</div>;
  }

  const byPlatform = new Map<string, MismatchGroup[]>();
  for (const g of data.groups) {
    if (!byPlatform.has(g.platform)) byPlatform.set(g.platform, []);
    byPlatform.get(g.platform)!.push(g);
  }

  return (
    <div className="space-y-4">
      <p className={cn(secondaryText, 'text-[11px] italic')}>{t('adminTesting.review.mismatchNameKeyCaption')}</p>
      {Array.from(byPlatform.entries()).map(([platform, groups]) => (
        <div key={platform}>
          <div className={cn(primaryText, semiBoldText, 'text-xs mb-1 font-mono')}>
            {platform} <span className={secondaryText}>({data.platformCounts[platform] ?? groups.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.mismatchColProduct')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.mismatchColPakbon')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.mismatchColFactuur')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.review.mismatchColOccurrences')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.mismatchColRestaurants')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.mismatchColExample')}</th>
                  <th className="py-1 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g, gi) => (
                  <tr key={`${g.matchKey}-${gi}`} className={cn('border-b border-gray-100 dark:border-gray-700', gi % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}>
                    <td className={cn(primaryText, 'py-1 px-2 max-w-xs')} title={g.productName}>
                      <div className="truncate">{g.productName}</div>
                      <div className={cn(secondaryText, 'font-mono text-[10px]')}>{g.articleId || g.matchKey}</div>
                    </td>
                    <td className={cn(secondaryText, 'py-1 px-2 font-mono whitespace-nowrap')}>{formatQuantityInfo(g.pakbonQuantityInfo)}</td>
                    <td className={cn('py-1 px-2 font-mono whitespace-nowrap text-indigo-600 dark:text-indigo-400')}>{formatQuantityInfo(g.factuurQuantityInfo)}</td>
                    <td className={cn(primaryText, 'py-1 px-2 text-right font-mono')}>{g.occurrences}</td>
                    <td className={cn(secondaryText, 'py-1 px-2 max-w-[16rem] truncate')} title={g.restaurantNames.join(', ')}>{g.restaurantNames.join(', ')}</td>
                    <td className={cn(secondaryText, 'py-1 px-2 font-mono whitespace-nowrap')}>{g.exampleOrderNumber} · {g.exampleOrderDate}</td>
                    <td className="py-1 px-2 text-right"><MismatchMonkeyButton group={g} t={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
